import { useCallback, useEffect, useRef, useState } from 'react';
import { MASCOT_ANIMATIONS_EVENT, MASCOT_ANIMATIONS_READY_EVENT, MASCOT_ANIMATIONS_SET_EVENT, MASCOT_ANIMATIONS_WAIT_MS, isMascotAnimationsUnavailable, parseMascotAnimationsSnapshot, } from './mascotAnimations.js';
const UNAVAILABLE = { state: 'unavailable', snapshot: null, pending: false, error: null, set: () => { } };
/**
 * The native "Mascot animations" setting as the desktop shell relays it (mascotAnimations.ts). Only with `enabled`
 * (the desktop workspace): elsewhere the state is 'unavailable' and nothing is asked. The widget shows only what the
 * agent last said; a change is a request, answered by the next snapshot, or an error when none comes in time.
 */
export function useMascotAnimations(enabled) {
    const [snapshot, setSnapshot] = useState(null);
    const [state, setState] = useState('loading');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const answer = useRef(undefined);
    const set = useCallback((next) => {
        if (!enabled)
            return;
        setPending(true);
        setError(null);
        clearTimeout(answer.current);
        answer.current = setTimeout(() => {
            setPending(false);
            setError('The desktop app did not answer. Try again.');
        }, MASCOT_ANIMATIONS_WAIT_MS);
        window.dispatchEvent(new CustomEvent(MASCOT_ANIMATIONS_SET_EVENT, { detail: { enabled: next } }));
    }, [enabled]);
    useEffect(() => {
        if (!enabled)
            return;
        setSnapshot(null);
        setState('loading');
        setPending(false);
        setError(null);
        const initial = setTimeout(() => setState((current) => (current === 'ready' ? current : 'unavailable')), MASCOT_ANIMATIONS_WAIT_MS);
        const receive = (event) => {
            const detail = event.detail;
            if (isMascotAnimationsUnavailable(detail)) {
                clearTimeout(initial);
                clearTimeout(answer.current);
                setSnapshot(null);
                setState('unavailable');
                setPending(false);
                setError(null);
                return;
            }
            const next = parseMascotAnimationsSnapshot(detail);
            if (!next)
                return;
            clearTimeout(initial);
            clearTimeout(answer.current);
            setSnapshot(next);
            setState('ready');
            setPending(false);
            setError(null);
        };
        window.addEventListener(MASCOT_ANIMATIONS_EVENT, receive);
        window.dispatchEvent(new CustomEvent(MASCOT_ANIMATIONS_READY_EVENT));
        return () => {
            window.removeEventListener(MASCOT_ANIMATIONS_EVENT, receive);
            clearTimeout(initial);
            clearTimeout(answer.current);
        };
    }, [enabled]);
    return enabled ? { state, snapshot, pending, error, set } : UNAVAILABLE;
}
//# sourceMappingURL=useMascotAnimations.js.map